import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useScope } from "@/hooks/use-scope";
import {
  getTenantTrace, updateTenantTrace,
  listKnownSerials, addKnownSerials, deleteKnownSerial,
  listKnownLots, addKnownLots, deleteKnownLot,
  listTraceExceptions, lookupSerial, lookupLot, recallImpact, openRecall,
} from "@/lib/traceability.functions";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Trash2, Upload, Search, ShieldCheck, AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/admin/traceability")({ component: TraceabilityAdmin });

const MODES = ["disabled", "optional", "required"] as const;
const POLICIES = ["none", "warn", "block", "override"] as const;
const STAGES = ["receive", "pick", "pack"] as const;

function TraceabilityAdmin() {
  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Traceability & Compliance</h2>
        <p className="text-sm text-muted-foreground">Configure serial, lot, and expiration tracking. Manage known-value allowlists and review scan exceptions.</p>
      </div>
      <Tabs defaultValue="defaults">
        <TabsList>
          <TabsTrigger value="defaults">Tenant defaults</TabsTrigger>
          <TabsTrigger value="serials">Known serials</TabsTrigger>
          <TabsTrigger value="lots">Known lots</TabsTrigger>
          <TabsTrigger value="exceptions">Scan exceptions</TabsTrigger>
          <TabsTrigger value="lookup">Trace lookup</TabsTrigger>
          <TabsTrigger value="recall">Recall impact</TabsTrigger>
        </TabsList>
        <TabsContent value="defaults" className="mt-4"><DefaultsTab /></TabsContent>
        <TabsContent value="serials" className="mt-4"><KnownSerialsTab /></TabsContent>
        <TabsContent value="lots" className="mt-4"><KnownLotsTab /></TabsContent>
        <TabsContent value="exceptions" className="mt-4"><ExceptionsTab /></TabsContent>
        <TabsContent value="lookup" className="mt-4"><LookupTab /></TabsContent>
        <TabsContent value="recall" className="mt-4"><RecallTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function DefaultsTab() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const get = useServerFn(getTenantTrace);
  const save = useServerFn(updateTenantTrace);
  const { data, isLoading } = useQuery({
    queryKey: ["trace-tenant", tenantId], enabled: !!tenantId,
    queryFn: () => get({ data: { tenantId: tenantId! } }),
  });
  const [draft, setDraft] = useState<any>(null);
  const cur = draft ?? data;

  const mut = useMutation({
    mutationFn: (patch: any) => save({ data: { tenantId: tenantId!, patch } }),
    onSuccess: () => { toast.success("Saved"); qc.invalidateQueries({ queryKey: ["trace-tenant"] }); setDraft(null); },
    onError: (e: any) => toast.error(e?.message ?? "Failed to save"),
  });

  if (isLoading || !cur) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const stages: string[] = cur.trace_capture_stages ?? [];

  return (
    <div className="grid gap-4 max-w-3xl">
      <Card>
        <CardHeader><CardTitle className="text-sm">Tracking Modes</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(["serial", "lot", "expiry"] as const).map((k) => (
            <div key={k} className="space-y-1.5">
              <Label className="capitalize text-xs">{k} tracking</Label>
              <Select value={cur[`trace_${k}_mode`]} onValueChange={(v) => setDraft({ ...cur, [`trace_${k}_mode`]: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm">Capture Stages</CardTitle></CardHeader>
        <CardContent className="flex gap-2">
          {STAGES.map((s) => {
            const on = stages.includes(s);
            return (
              <Button key={s} variant={on ? "default" : "outline"} size="sm" onClick={() =>
                setDraft({ ...cur, trace_capture_stages: on ? stages.filter((x) => x !== s) : [...stages, s] })
              }>{s}</Button>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm">Known-Value Enforcement</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(["serial", "lot"] as const).map((k) => (
            <div key={k} className="space-y-1.5">
              <Label className="capitalize text-xs">Known {k} policy</Label>
              <Select value={cur[`trace_known_${k}_policy`]} onValueChange={(v) => setDraft({ ...cur, [`trace_known_${k}_policy`]: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{POLICIES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" disabled={!draft} onClick={() => setDraft(null)}>Reset</Button>
        <Button disabled={!draft || mut.isPending} onClick={() => mut.mutate({
          serial_mode: cur.trace_serial_mode, lot_mode: cur.trace_lot_mode, expiry_mode: cur.trace_expiry_mode,
          capture_stages: cur.trace_capture_stages, known_serial_policy: cur.trace_known_serial_policy, known_lot_policy: cur.trace_known_lot_policy,
        })}>{mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button>
      </div>
    </div>
  );
}

function CustomerPicker({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  const { tenantId, customers } = useScope();
  const list = customers.filter((c) => c.tenant_id === tenantId);
  return (
    <Select value={value ?? ""} onValueChange={(v) => onChange(v || null)}>
      <SelectTrigger className="w-[240px]"><SelectValue placeholder="Select customer…" /></SelectTrigger>
      <SelectContent>{list.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function SkuPicker({ tenantId, customerId, value, onChange }: { tenantId: string | null; customerId: string | null; value: string | null; onChange: (v: string | null) => void }) {
  const { data: skus = [] } = useQuery({
    queryKey: ["scope-skus", tenantId, customerId],
    enabled: !!tenantId && !!customerId,
    queryFn: async () => (await supabase.from("skus").select("id, sku_code, name").eq("tenant_id", tenantId!).eq("customer_id", customerId!).order("sku_code").limit(500)).data ?? [],
  });
  return (
    <Select value={value ?? "__all__"} onValueChange={(v) => onChange(v === "__all__" ? null : v)}>
      <SelectTrigger className="w-[280px]"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">All SKUs (customer-wide)</SelectItem>
        {skus.map((s: any) => <SelectItem key={s.id} value={s.id}>{s.sku_code} — {s.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function KnownSerialsTab() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const list = useServerFn(listKnownSerials);
  const add = useServerFn(addKnownSerials);
  const del = useServerFn(deleteKnownSerial);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [skuId, setSkuId] = useState<string | null>(null);
  const [paste, setPaste] = useState("");
  const [q, setQ] = useState("");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["known-serials", tenantId, customerId, skuId, q],
    enabled: !!tenantId,
    queryFn: () => list({ data: { tenantId: tenantId!, customerId, skuId, q: q || undefined } }),
  });

  const upload = useMutation({
    mutationFn: async () => {
      const serials = paste.split(/\r?\n|,/).map((s) => s.trim()).filter(Boolean);
      if (!serials.length) throw new Error("Paste at least one serial");
      if (!customerId) throw new Error("Pick a customer");
      return add({ data: { tenantId: tenantId!, customerId, skuId, serials, source: "manual" } });
    },
    onSuccess: (r) => { toast.success(`Added ${r.added} serial(s)`); setPaste(""); qc.invalidateQueries({ queryKey: ["known-serials"] }); },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-sm">Upload Serials</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <CustomerPicker value={customerId} onChange={(v) => { setCustomerId(v); setSkuId(null); }} />
            <SkuPicker tenantId={tenantId} customerId={customerId} value={skuId} onChange={setSkuId} />
          </div>
          <Textarea value={paste} onChange={(e) => setPaste(e.target.value)} placeholder="Paste serials, one per line or comma-separated" rows={5} />
          <div className="flex justify-end">
            <Button onClick={() => upload.mutate()} disabled={upload.isPending || !customerId}>
              {upload.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Add to allowlist
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search serials…" className="max-w-xs" />
        <span className="text-xs text-muted-foreground">{rows.length} record(s)</span>
      </div>

      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Serial</TableHead><TableHead>Source</TableHead><TableHead>Uploaded</TableHead><TableHead className="w-12" /></TableRow></TableHeader>
          <TableBody>
            {isLoading ? <TableRow><TableCell colSpan={4} className="text-center py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow> :
             rows.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-6 text-sm text-muted-foreground">No approved serials yet.</TableCell></TableRow> :
             rows.map((r: any) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs">{r.serial_value}</TableCell>
                <TableCell className="text-xs">{r.source ?? "—"}</TableCell>
                <TableCell className="text-xs">{new Date(r.uploaded_at).toLocaleString()}</TableCell>
                <TableCell><Button size="icon" variant="ghost" onClick={async () => { await del({ data: { id: r.id } }); qc.invalidateQueries({ queryKey: ["known-serials"] }); }}><Trash2 className="h-3.5 w-3.5" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function KnownLotsTab() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const list = useServerFn(listKnownLots);
  const add = useServerFn(addKnownLots);
  const del = useServerFn(deleteKnownLot);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [skuId, setSkuId] = useState<string | null>(null);
  const [paste, setPaste] = useState("");
  const [q, setQ] = useState("");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["known-lots", tenantId, customerId, skuId, q],
    enabled: !!tenantId,
    queryFn: () => list({ data: { tenantId: tenantId!, customerId, skuId, q: q || undefined } }),
  });

  const upload = useMutation({
    mutationFn: async () => {
      const lots = paste.split(/\r?\n/).map((line) => {
        const [code, exp] = line.split(/[,;\t]/).map((s) => s.trim());
        return code ? { lot_code: code, expiry_date: exp || null } : null;
      }).filter(Boolean) as { lot_code: string; expiry_date: string | null }[];
      if (!lots.length) throw new Error("Paste at least one lot");
      if (!customerId) throw new Error("Pick a customer");
      return add({ data: { tenantId: tenantId!, customerId, skuId, lots, source: "manual" } });
    },
    onSuccess: (r) => { toast.success(`Added ${r.added} lot(s)`); setPaste(""); qc.invalidateQueries({ queryKey: ["known-lots"] }); },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-sm">Upload Lots</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <CustomerPicker value={customerId} onChange={(v) => { setCustomerId(v); setSkuId(null); }} />
            <SkuPicker tenantId={tenantId} customerId={customerId} value={skuId} onChange={setSkuId} />
          </div>
          <Textarea value={paste} onChange={(e) => setPaste(e.target.value)} placeholder="LOT-A,2027-12-31&#10;LOT-B,2028-06-30" rows={5} />
          <div className="flex justify-end">
            <Button onClick={() => upload.mutate()} disabled={upload.isPending || !customerId}>
              {upload.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              Add to allowlist
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search lots…" className="max-w-xs" />
        <span className="text-xs text-muted-foreground">{rows.length} record(s)</span>
      </div>

      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Lot</TableHead><TableHead>Expiry</TableHead><TableHead>Source</TableHead><TableHead>Uploaded</TableHead><TableHead className="w-12" /></TableRow></TableHeader>
          <TableBody>
            {isLoading ? <TableRow><TableCell colSpan={5} className="text-center py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow> :
             rows.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-6 text-sm text-muted-foreground">No approved lots yet.</TableCell></TableRow> :
             rows.map((r: any) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs">{r.lot_code}</TableCell>
                <TableCell className="text-xs">{r.expiry_date ?? "—"}</TableCell>
                <TableCell className="text-xs">{r.source ?? "—"}</TableCell>
                <TableCell className="text-xs">{new Date(r.uploaded_at).toLocaleString()}</TableCell>
                <TableCell><Button size="icon" variant="ghost" onClick={async () => { await del({ data: { id: r.id } }); qc.invalidateQueries({ queryKey: ["known-lots"] }); }}><Trash2 className="h-3.5 w-3.5" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ExceptionsTab() {
  const { tenantId, customerId } = useScope();
  const list = useServerFn(listTraceExceptions);
  const [stage, setStage] = useState<string>("");
  const [hours, setHours] = useState<number>(168);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["trace-exc", tenantId, customerId, stage, hours],
    enabled: !!tenantId,
    queryFn: () => list({ data: { tenantId: tenantId!, customerId: customerId ?? null, stage: (stage as any) || null, sinceHours: hours } }),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="All stages" /></SelectTrigger>
          <SelectContent><SelectItem value="">All stages</SelectItem>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(hours)} onValueChange={(v) => setHours(Number(v))}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="24">Last 24 h</SelectItem>
            <SelectItem value="168">Last 7 days</SelectItem>
            <SelectItem value="720">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Stage</TableHead><TableHead>Outcome</TableHead><TableHead>Serial</TableHead><TableHead>Lot</TableHead><TableHead>Reason</TableHead></TableRow></TableHeader>
          <TableBody>
            {isLoading ? <TableRow><TableCell colSpan={6} className="text-center py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow> :
             rows.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-sm text-muted-foreground">No exceptions in window.</TableCell></TableRow> :
             rows.map((r: any, i: number) => (
              <TableRow key={i}>
                <TableCell className="text-xs whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</TableCell>
                <TableCell><Badge variant="outline">{r.stage}</Badge></TableCell>
                <TableCell><Badge variant={r.outcome === "fail" ? "destructive" : "secondary"}>{r.outcome}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{r.serial_value ?? "—"}</TableCell>
                <TableCell className="font-mono text-xs">{r.lot_code ?? "—"}</TableCell>
                <TableCell className="text-xs">{r.reason ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function LookupTab() {
  const { tenantId } = useScope();
  const ser = useServerFn(lookupSerial);
  const lot = useServerFn(lookupLot);
  const [serial, setSerial] = useState("");
  const [lotCode, setLotCode] = useState("");
  const [serRes, setSerRes] = useState<any>(null);
  const [lotRes, setLotRes] = useState<any>(null);

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Card>
        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Search className="h-4 w-4" /> Serial lookup</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="Scan or type serial" />
            <Button onClick={async () => setSerRes(await ser({ data: { tenantId: tenantId!, serial } }))}>Look up</Button>
          </div>
          {serRes && (serRes.serial ? (
            <div className="text-xs space-y-1">
              <div><b>Status:</b> {serRes.serial.status} ({serRes.serial.current_stage ?? "—"})</div>
              <div><b>Lot:</b> {serRes.serial.lot ?? "—"}</div>
              <div><b>Allocation:</b> {serRes.serial.allocation_id ?? "—"}</div>
              <div><b>Order:</b> {serRes.serial.order_id ?? "—"}</div>
              <div><b>Shipment:</b> {serRes.serial.shipment_id ?? "—"} {serRes.serial.shipped_at ? `(${new Date(serRes.serial.shipped_at).toLocaleString()})` : ""}</div>
              <div className="pt-2 font-medium">Recent scans</div>
              <ul className="space-y-0.5">
                {serRes.audit.slice(0, 10).map((a: any, i: number) => (
                  <li key={i}>{new Date(a.created_at).toLocaleString()} · {a.stage} · {a.outcome}{a.reason ? ` · ${a.reason}` : ""}</li>
                ))}
              </ul>
            </div>
          ) : <p className="text-xs text-muted-foreground">Not found.</p>)}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Search className="h-4 w-4" /> Lot lookup</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input value={lotCode} onChange={(e) => setLotCode(e.target.value)} placeholder="Lot code" />
            <Button onClick={async () => setLotRes(await lot({ data: { tenantId: tenantId!, lotCode } }))}>Look up</Button>
          </div>
          {lotRes && (lotRes.impact.length ? (
            <div className="text-xs space-y-2">
              {lotRes.impact.map((r: any) => (
                <div key={r.lot_id} className="border rounded p-2">
                  <div><b>Received:</b> {r.received_qty} · <b>On hand:</b> {r.on_hand_qty} · <b>Allocated:</b> {r.allocated_qty} · <b>Packed:</b> {r.packed_qty}</div>
                  <div><b>Orders:</b> {r.orders_count} · <b>Customers:</b> {r.customers_count}</div>
                  {r.expiry_date && <div><b>Expires:</b> {r.expiry_date}</div>}
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-muted-foreground">No lot found.</p>)}
        </CardContent>
      </Card>
    </div>
  );
}

function RecallTab() {
  const { tenantId } = useScope();
  const fn = useServerFn(recallImpact);
  const open = useServerFn(openRecall);
  const [lotCode, setLotCode] = useState("");
  const [serials, setSerials] = useState("");
  const [res, setRes] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [severity, setSeverity] = useState<"low" | "medium" | "high" | "critical">("medium");
  const [opening, setOpening] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const r = await fn({ data: {
        tenantId: tenantId!,
        lotCode: lotCode || undefined,
        serials: serials ? serials.split(/\r?\n|,/).map((s) => s.trim()).filter(Boolean) : undefined,
      } });
      setRes(r);
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
    finally { setLoading(false); }
  };

  const fileRecall = async () => {
    if (!reason.trim()) { toast.error("Recall reason required"); return; }
    setOpening(true);
    try {
      const r = await open({ data: {
        tenantId: tenantId!,
        lotCode: lotCode || null,
        serials: serials ? serials.split(/\r?\n|,/).map((s) => s.trim()).filter(Boolean) : undefined,
        reason: reason.trim(), severity,
      } });
      toast.success(`Recall opened · ${r.affectedOrders} order(s) flagged`);
      setReason("");
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
    finally { setOpening(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <Card>
        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-warning" /> Recall impact preview</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Lot code</Label><Input value={lotCode} onChange={(e) => setLotCode(e.target.value)} placeholder="LOT-XYZ" /></div>
            <div><Label className="text-xs">Or serials (one per line)</Label><Textarea value={serials} onChange={(e) => setSerials(e.target.value)} rows={3} /></div>
          </div>
          <div className="flex justify-end">
            <Button onClick={run} disabled={loading || (!lotCode && !serials)}>{loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Preview impact</Button>
          </div>
          {res && (
            <div className="text-xs space-y-2 pt-2 border-t">
              <div className="flex gap-4"><span><b>Orders:</b> {res.orders.length}</span><span><b>Customers:</b> {res.customers_count}</span><span><b>Allocations:</b> {res.allocations.length}</span><span><b>Serials matched:</b> {res.serials.length}</span></div>
              <div className="rounded border overflow-hidden">
                <Table>
                  <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {res.orders.slice(0, 50).map((o: any) => (
                      <TableRow key={o.id}><TableCell className="font-mono text-xs">{o.order_number}</TableCell><TableCell>{o.status}</TableCell><TableCell className="text-xs">{new Date(o.created_at).toLocaleString()}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="pt-3 border-t space-y-2">
                <div className="text-sm font-medium">Open formal recall</div>
                <div className="grid md:grid-cols-[1fr_160px] gap-2">
                  <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for recall (regulator-facing)" rows={2} />
                  <Select value={severity} onValueChange={(v) => setSeverity(v as any)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(["low","medium","high","critical"] as const).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end">
                  <Button variant="destructive" disabled={opening || !reason.trim()} onClick={fileRecall}>
                    {opening ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <AlertTriangle className="h-4 w-4 mr-2" />}
                    Open recall · flag {res.orders.length} order(s)
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}